import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import api from '../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop, faFilePdf, faPlayCircle, faUpload, faList, faHistory, faChartPie, faCheckCircle, faTimesCircle, faClock, faSpinner, faSync, faBalanceScale } from '@fortawesome/free-solid-svg-icons';

const MESES = ['', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

const fmtMonto = (n) => {
    if (n == null || n === '') return '—';
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n);
};

function TablaPropuestaSII({ propuesta }) {
    if (!propuesta?.lineas?.length) {
        return <p className="text-sm text-gray-500 italic py-2">Sin propuesta sincronizada para este período.</p>;
    }
    let seccionActual = '';
    return (
        <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
            {propuesta.cabecera?.texto && (
                <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 text-sm text-blue-900">
                    {propuesta.cabecera.texto}
                </div>
            )}
            <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                    <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-600">Glosa</th>
                        <th className="px-3 py-2 text-center font-semibold text-gray-600 w-16">Cód.</th>
                        <th className="px-3 py-2 text-right font-semibold text-gray-600 w-32">Valor</th>
                    </tr>
                </thead>
                <tbody>
                    {propuesta.lineas.map((l, i) => {
                        const showSeccion = l.seccion && l.seccion !== seccionActual;
                        if (showSeccion) seccionActual = l.seccion;
                        return (
                            <React.Fragment key={i}>
                                {showSeccion && (
                                    <tr className="bg-blue-600 text-white">
                                        <td colSpan="3" className="px-3 py-1.5 font-bold text-xs uppercase tracking-wide">{l.seccion}</td>
                                    </tr>
                                )}
                                {l.subseccion && i > 0 && propuesta.lineas[i - 1]?.subseccion !== l.subseccion && (
                                    <tr className="bg-blue-100">
                                        <td colSpan="3" className="px-3 py-1 text-xs font-semibold text-blue-800">{l.subseccion}</td>
                                    </tr>
                                )}
                                <tr className={l.esTotal ? 'bg-blue-50 font-bold' : 'hover:bg-gray-50'}>
                                    <td className="px-3 py-1 text-gray-800">{l.operador && <span className="text-gray-400 mr-1">{l.operador}</span>}{l.glosa}</td>
                                    <td className="px-3 py-1 text-center text-gray-500">{l.codigo || '—'}</td>
                                    <td className="px-3 py-1 text-right text-gray-900">{l.valorTexto || (l.valor ? fmtMonto(l.valor) : '—')}</td>
                                </tr>
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function PanelF29Admin() {
    const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, lista, historial
    const [empresas, setEmpresas] = useState([]);
    const [logs, setLogs] = useState({});
    const [propuestas, setPropuestas] = useState({});
    const [cruces, setCruces] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedLogId, setExpandedLogId] = useState(null);
    const [selectedEmpresas, setSelectedEmpresas] = useState(new Set());
    
    // Selectores
    const [selectedMes, setSelectedMes] = useState(new Date().getMonth() || 12);
    const [selectedAnio, setSelectedAnio] = useState(selectedMes === 12 ? new Date().getFullYear() - 1 : new Date().getFullYear());
    const fileInputRef = useRef(null);

    const [isUploading, setIsUploading] = useState(false);
    const [isExecuting, setIsExecuting] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);

    useEffect(() => {
        cargarEmpresas();
    }, []);

    useEffect(() => {
        if (empresas.length > 0) {
            localStorage.setItem('selectedEmpresasF29', JSON.stringify(Array.from(selectedEmpresas)));
        }
    }, [selectedEmpresas, empresas]);

    useEffect(() => {
        cargarLogs();
        cargarPropuestas();
        cargarCruces();
        const interval = setInterval(() => {
            cargarLogs();
            cargarPropuestas();
            cargarCruces();
        }, 5000);
        return () => clearInterval(interval);
    }, [selectedMes, selectedAnio]);

    const cargarEmpresas = async () => {
        try {
            const res = await api.get('/f29/listado');
            setEmpresas(res.data);
            
            const saved = localStorage.getItem('selectedEmpresasF29');
            if (saved) {
                const parsed = JSON.parse(saved);
                const validIds = res.data.map(e => e._id);
                const filtered = parsed.filter(id => validIds.includes(id));
                setSelectedEmpresas(new Set(filtered));
            } else {
                setSelectedEmpresas(new Set(res.data.map(e => e._id)));
            }
        } catch (error) {
            toast.error('Error al cargar lista de empresas F29');
        }
    };

    const cargarLogs = async () => {
        if (!selectedMes || !selectedAnio) return;
        try {
            const res = await api.get(`/f29/logs/${selectedAnio}/${selectedMes}`);
            const logsMap = {};
            res.data.forEach(log => {
                logsMap[log.empresaId] = log;
            });
            setLogs(logsMap);
        } catch (error) {
            console.error('Error cargando logs F29', error);
        }
    };

    const cargarPropuestas = async () => {
        if (!selectedMes || !selectedAnio) return;
        try {
            const res = await api.get(`/f29/propuestas/${selectedAnio}/${selectedMes}`);
            const map = {};
            res.data.forEach(p => { map[p.empresaId] = p; });
            setPropuestas(map);
        } catch (error) {
            console.error('Error cargando propuestas F29', error);
        }
    };

    const cargarCruces = async () => {
        if (!selectedMes || !selectedAnio) return;
        try {
            const res = await api.get(`/f29/cruces/${selectedAnio}/${selectedMes}`);
            const map = {};
            res.data.forEach(c => { map[c.empresaId] = c; });
            setCruces(map);
        } catch (error) {
            console.error('Error cargando cruces F29', error);
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        setIsUploading(true);
        try {
            const res = await api.post('/f29/upload-csv', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success(res.data.message);
            cargarEmpresas();
            setActiveTab('lista');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al subir el archivo');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const ejecutarMasivo = async () => {
        if (empresas.length === 0) return toast.warning('No hay empresas cargadas.');
        if (selectedEmpresas.size === 0) return toast.warning('Selecciona al menos una empresa.');
        if (!window.confirm(`¿Estás seguro de iniciar la declaración automática para ${selectedEmpresas.size} empresas en el período ${MESES[selectedMes]} ${selectedAnio}?`)) return;

        setIsExecuting(true);
        try {
            const res = await api.post('/f29/ejecutar', {
                mes: selectedMes,
                anio: selectedAnio,
                empresasIds: Array.from(selectedEmpresas)
            });
            toast.success(res.data.message);
            cargarLogs();
        } catch (error) {
            toast.error('Error al iniciar la ejecución masiva');
        } finally {
            setIsExecuting(false);
        }
    };

    const toggleAllEmpresas = () => {
        if (selectedEmpresas.size === empresas.length) {
            setSelectedEmpresas(new Set());
        } else {
            setSelectedEmpresas(new Set(empresas.map(e => e._id)));
        }
    };

    const toggleEmpresa = (id, event) => {
        event.stopPropagation();
        const newSet = new Set(selectedEmpresas);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setSelectedEmpresas(newSet);
    };

    const detenerEjecucion = async () => {
        if (!window.confirm(`¿Estás seguro de detener el proceso actual? Se cancelarán las tareas en cola.`)) return;
        try {
            const res = await api.post('/f29/detener');
            toast.success(res.data.message);
            cargarLogs();
            setIsExecuting(false);
        } catch (error) {
            toast.error('Error al detener la ejecución');
        }
    };

    const ejecutarIndividual = async (empresaId) => {
        try {
            await api.post('/f29/ejecutar', {
                empresasIds: [empresaId],
                mes: selectedMes,
                anio: selectedAnio
            });
            toast.success('Empresa encolada');
            cargarLogs();
        } catch (error) {
            toast.error('Error al encolar la empresa');
        }
    };

    const syncPropuestaMasivo = async () => {
        if (empresas.length === 0) return toast.warning('No hay empresas cargadas.');
        if (selectedEmpresas.size === 0) return toast.warning('Selecciona al menos una empresa.');
        if (!window.confirm(`¿Sincronizar propuesta SII para ${selectedEmpresas.size} empresas (${MESES[selectedMes]} ${selectedAnio})?`)) return;

        setIsSyncing(true);
        try {
            const res = await api.post('/f29/sync-propuesta', {
                mes: selectedMes,
                anio: selectedAnio,
                empresasIds: Array.from(selectedEmpresas)
            });
            toast.success(res.data.message);
            cargarPropuestas();
            cargarCruces();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al sincronizar propuestas');
        } finally {
            setIsSyncing(false);
        }
    };

    const syncPropuestaIndividual = async (empresaId) => {
        try {
            const res = await api.post('/f29/sync-propuesta', {
                mes: selectedMes,
                anio: selectedAnio,
                empresasIds: [empresaId]
            });
            toast.success(res.data.message || 'Sync iniciado');
            cargarPropuestas();
            cargarCruces();
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al sincronizar propuesta');
        }
    };

    const toggleActivaDeclaracion = async (empresaId, valorActual, e) => {
        e.stopPropagation();
        try {
            const nuevo = !(valorActual !== false);
            const res = await api.patch(`/f29/empresa/${empresaId}/activa-declaracion`, {
                activaDeclaracion: nuevo
            });
            setEmpresas(prev => prev.map(emp => emp._id === empresaId ? { ...emp, activaDeclaracion: res.data.activaDeclaracion } : emp));
            toast.success(nuevo ? 'Incluida en cola día 8' : 'Excluida de cola día 8');
        } catch (error) {
            toast.error('Error al actualizar toggle');
        }
    };

    const getEstadoBadge = (log) => {
        if (!log || !log.estado) return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">Pendiente</span>;
        switch (log.estado) {
            case 'en_cola': return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800"><FontAwesomeIcon icon={faClock} className="mr-1" /> En Cola</span>;
            case 'en_progreso': return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800"><FontAwesomeIcon icon={faSpinner} spin className="mr-1" /> Procesando</span>;
            case 'completado': 
                if (log.resultado?.type === 'ya_declarado') {
                    return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 shadow-sm"><FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Ya Declarado</span>;
                }
                return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"><FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Completado</span>;
            case 'ya_declarado':
                return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 shadow-sm"><FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Ya Declarado</span>;
            case 'error': return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800"><FontAwesomeIcon icon={faTimesCircle} className="mr-1" /> Error</span>;
            default: return <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">Pendiente</span>;
        }
    };

    const descargarPdf = (logId) => {
        const token = localStorage.getItem('token');
        const url = `${api.defaults.baseURL}/f29/descargar-pdf/${logId}?token=${token}`;
        window.open(url, '_blank');
    };

    const getPropuestaBadge = (prop) => {
        if (!prop || !prop.estadoSync || prop.estadoSync === 'sin_propuesta') {
            return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Sin sync</span>;
        }
        switch (prop.estadoSync) {
            case 'ok': return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">OK</span>;
            case 'en_progreso': return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800"><FontAwesomeIcon icon={faSpinner} spin className="mr-1" />Sync</span>;
            case 'bloqueada': return <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800">Declarado</span>;
            case 'error': return <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800" title={prop.errorMensaje}>Error</span>;
            default: return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">{prop.estadoSync}</span>;
        }
    };

    const getCruceBadge = (cruce) => {
        if (!cruce) return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">—</span>;
        if (cruce.sinClienteContable) return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Sin cliente</span>;
        if (!cruce.tienePropuesta) return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">Sin propuesta</span>;
        if (cruce.cuadra) return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800"><FontAwesomeIcon icon={faCheckCircle} className="mr-1" />OK</span>;
        if (cruce.desajustes > 0) return <span className="px-2 py-0.5 text-xs rounded-full bg-orange-100 text-orange-800">{cruce.desajustes} diff</span>;
        return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">Sin datos</span>;
    };

    // Calcular estadísticas para el dashboard
    const stats = { total: empresas.length, completados: 0, errores: 0, en_cola: 0, pendientes: 0 };
    empresas.forEach(emp => {
        const estado = logs[emp._id]?.estado;
        if (estado === 'completado' || estado === 'ya_declarado') stats.completados++;
        else if (estado === 'error') stats.errores++;
        else if (estado === 'en_cola' || estado === 'en_progreso') stats.en_cola++;
        else stats.pendientes++;
    });

    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto bg-gray-50 min-h-screen">
            <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div>
                    <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Módulo F29</h1>
                    <p className="text-gray-500 text-sm mt-1">Gestión mensual: propuesta SII, cruce contable y declaración automática</p>
                </div>
                
                {/* Selectores de Período Globales */}
                <div className="mt-4 md:mt-0 flex gap-3 items-center bg-gray-50 p-2 rounded-lg border border-gray-200">
                    <div>
                        <select 
                            value={selectedMes} 
                            onChange={(e) => setSelectedMes(Number(e.target.value))}
                            className="block w-full pl-3 pr-8 py-2 text-sm font-medium bg-white border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md shadow-sm"
                        >
                            {MESES.map((m, i) => i > 0 && <option key={i} value={i}>{m}</option>)}
                        </select>
                    </div>
                    <div>
                        <input 
                            type="number" 
                            value={selectedAnio} 
                            onChange={(e) => setSelectedAnio(Number(e.target.value))}
                            className="block w-20 pl-3 pr-3 py-2 text-sm font-medium bg-white border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md shadow-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Pestañas de Navegación */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('dashboard')}
                        className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                            activeTab === 'dashboard'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <FontAwesomeIcon icon={faChartPie} className="mr-2" />
                        Dashboard F29
                    </button>
                    <button
                        onClick={() => setActiveTab('historial')}
                        className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                            activeTab === 'historial'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                    >
                        <FontAwesomeIcon icon={faHistory} className="mr-2" />
                        Historial (Logs)
                    </button>
                </nav>
            </div>

            {/* TAB: DASHBOARD (Muestra Stats y la Lista de Empresas) */}
            {activeTab === 'dashboard' && (
                <div className="space-y-6">
                    {/* Estadísticas */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-500">Total Empresas</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                            </div>
                            <div className="p-3 bg-gray-100 rounded-full text-gray-600">
                                <FontAwesomeIcon icon={faList} size="lg" />
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-green-600">Completados</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.completados}</p>
                            </div>
                            <div className="p-3 bg-green-100 rounded-full text-green-600">
                                <FontAwesomeIcon icon={faCheckCircle} size="lg" />
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-red-600">Errores</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.errores}</p>
                            </div>
                            <div className="p-3 bg-red-100 rounded-full text-red-600">
                                <FontAwesomeIcon icon={faTimesCircle} size="lg" />
                            </div>
                        </div>
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-blue-600">En Progreso/Cola</p>
                                <p className="text-3xl font-bold text-gray-900">{stats.en_cola}</p>
                            </div>
                            <div className="p-3 bg-blue-100 rounded-full text-blue-600">
                                <FontAwesomeIcon icon={faClock} size="lg" />
                            </div>
                        </div>
                    </div>

                    {/* Upload CSV y Acciones */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="w-full md:w-1/2">
                            <label className="block text-sm font-bold text-gray-700 mb-2">Cargar Lista Maestra (CSV)</label>
                            <div className="flex items-center gap-3">
                                <label className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-200 transition-colors w-full">
                                    <FontAwesomeIcon icon={faUpload} className="text-gray-500" />
                                    <span className="text-sm font-medium text-gray-700">Seleccionar Archivo</span>
                                    <input 
                                        type="file" 
                                        accept=".csv"
                                        ref={fileInputRef}
                                        className="hidden"
                                        onChange={handleFileUpload}
                                        disabled={isUploading}
                                    />
                                </label>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-2 w-full md:w-auto">
                            <button 
                                onClick={syncPropuestaMasivo}
                                disabled={empresas.length === 0 || isSyncing}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-all w-full md:w-auto shadow-sm"
                            >
                                <FontAwesomeIcon icon={faSync} spin={isSyncing} />
                                Sync Propuesta SII
                            </button>
                            <button 
                                onClick={ejecutarMasivo}
                                disabled={empresas.length === 0}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-all w-full md:w-auto shadow-sm"
                            >
                                <FontAwesomeIcon icon={faPlayCircle} />
                                Declarar F29
                            </button>
                            <button 
                                onClick={detenerEjecucion}
                                className="bg-red-100 hover:bg-red-200 text-red-700 px-5 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all w-full md:w-auto"
                            >
                                <FontAwesomeIcon icon={faStop} />
                                Detener
                            </button>
                        </div>
                    </div>

                    {/* Tabla de Resultados */}
                    <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-4 text-left">
                                            <input 
                                                type="checkbox" 
                                                checked={empresas.length > 0 && selectedEmpresas.size === empresas.length}
                                                onChange={toggleAllEmpresas}
                                                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                            />
                                        </th>
                                        <th className="px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">RUT</th>
                                        <th className="px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Empresa</th>
                                        <th className="px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Propuesta</th>
                                        <th className="px-4 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Cód. 91</th>
                                        <th className="px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Declaración</th>
                                        <th className="px-4 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Día 8</th>
                                        <th className="px-4 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Cruce</th>
                                        <th className="px-4 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {empresas
                                        .sort((a, b) => a.nombre.localeCompare(b.nombre))
                                        .map(emp => {
                                        const log = logs[emp._id];
                                        const prop = propuestas[emp._id];
                                        const cruce = cruces[emp._id];
                                        const activa = emp.activaDeclaracion !== false;
                                        return (
                                            <React.Fragment key={emp._id}>
                                                <tr 
                                                    className={`transition-colors cursor-pointer ${log?.estado === 'en_progreso' ? 'bg-yellow-50/60 hover:bg-yellow-100/60' : 'hover:bg-blue-50'}`}
                                                    onClick={() => setExpandedLogId(expandedLogId === emp._id ? null : emp._id)}
                                                >
                                                    <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                        <input 
                                                            type="checkbox" 
                                                            checked={selectedEmpresas.has(emp._id)}
                                                            onChange={(e) => toggleEmpresa(emp._id, e)}
                                                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-400 text-xs w-3">{expandedLogId === emp._id ? '▼' : '▶'}</span>
                                                            {emp.rut}
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 max-w-[200px] truncate" title={emp.nombre}>{emp.nombre}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap">{getPropuestaBadge(prop)}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">{fmtMonto(prop?.montoAPagar ?? prop?.codigo91)}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap">{getEstadoBadge(log)}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                                                        <button
                                                            onClick={(e) => toggleActivaDeclaracion(emp._id, emp.activaDeclaracion, e)}
                                                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${activa ? 'bg-green-500' : 'bg-gray-300'}`}
                                                            title={activa ? 'Activa en cola día 8' : 'Excluida de cola día 8'}
                                                        >
                                                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${activa ? 'translate-x-6' : 'translate-x-1'}`} />
                                                        </button>
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap">{getCruceBadge(cruce)}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium" onClick={e => e.stopPropagation()}>
                                                        <div className="flex items-center justify-end gap-2">
                                                            <button
                                                                onClick={() => syncPropuestaIndividual(emp._id)}
                                                                className="px-2 py-1 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-md text-xs border border-indigo-200"
                                                                title="Sync propuesta SII"
                                                            >
                                                                <FontAwesomeIcon icon={faSync} />
                                                            </button>
                                                            {log?.resultado?.rutaPdf && (
                                                                <button 
                                                                    onClick={() => descargarPdf(log._id)}
                                                                    className="px-2 py-1 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-md text-xs border border-blue-200"
                                                                    title="Descargar Certificado"
                                                                >
                                                                    <FontAwesomeIcon icon={faFilePdf} />
                                                                </button>
                                                            )}
                                                            {(!log || log.estado === 'error' || log.estado === 'completado') && (
                                                                <button 
                                                                    onClick={() => ejecutarIndividual(emp._id)}
                                                                    className="px-2 py-1 bg-gray-100 hover:bg-blue-600 hover:text-white text-gray-700 rounded-md text-xs font-bold"
                                                                    title="Declarar F29"
                                                                >
                                                                    F29
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                                {expandedLogId === emp._id && (
                                                    <tr>
                                                        <td colSpan="9" className="px-4 py-4 bg-gray-50 border-b border-gray-200">
                                                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                                                                <div>
                                                                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                                                        <FontAwesomeIcon icon={faList} /> Propuesta SII
                                                                        {prop?.fechaActualizacion && (
                                                                            <span className="font-normal text-gray-400 normal-case">
                                                                                · {new Date(prop.fechaActualizacion).toLocaleString('es-CL')}
                                                                            </span>
                                                                        )}
                                                                    </h4>
                                                                    <TablaPropuestaSII propuesta={prop} />
                                                                </div>
                                                                <div className="space-y-4">
                                                                    {cruce && cruce.items?.length > 0 && (
                                                                        <div>
                                                                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                                                                <FontAwesomeIcon icon={faBalanceScale} /> Cruce contable — {cruce.resumen}
                                                                            </h4>
                                                                            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white text-sm">
                                                                                <table className="min-w-full">
                                                                                    <thead className="bg-gray-100">
                                                                                        <tr>
                                                                                            <th className="px-3 py-1.5 text-left text-xs">Concepto</th>
                                                                                            <th className="px-3 py-1.5 text-right text-xs">F29</th>
                                                                                            <th className="px-3 py-1.5 text-right text-xs">Contab.</th>
                                                                                            <th className="px-3 py-1.5 text-center text-xs">OK</th>
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {cruce.items.map((item, i) => (
                                                                                            <tr key={i} className={item.cuadra ? '' : 'bg-orange-50'}>
                                                                                                <td className="px-3 py-1 text-gray-700">{item.label}</td>
                                                                                                <td className="px-3 py-1 text-right">{fmtMonto(item.propuesta)}</td>
                                                                                                <td className="px-3 py-1 text-right">{fmtMonto(item.contabilidad)}</td>
                                                                                                <td className="px-3 py-1 text-center">{item.cuadra ? '✓' : '✗'}</td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    <div>
                                                                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Terminal declaración</h4>
                                                                        <div className="bg-slate-900 rounded-lg p-4 shadow-inner max-h-[300px] overflow-y-auto">
                                                                            {log?.logEntries?.length > 0 ? (
                                                                                <div className="space-y-1 font-mono text-xs">
                                                                                    {log.logEntries.map((entry, i) => (
                                                                                        <div key={i} className={`${entry.includes('ERROR') || entry.includes('❌') ? 'text-red-400 font-bold' : entry.includes('✅') ? 'text-green-400' : 'text-gray-300'} leading-relaxed`}>{entry}</div>
                                                                                    ))}
                                                                                </div>
                                                                            ) : (
                                                                                <p className="text-slate-500 text-sm italic py-2">Sin logs de declaración para este período.</p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    {empresas.length === 0 && (
                                        <tr>
                                            <td colSpan="9" className="px-6 py-16 text-center">
                                                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                                    <FontAwesomeIcon icon={faList} className="text-gray-400 text-xl" />
                                                </div>
                                                <p className="text-gray-500 font-medium">No hay empresas cargadas en la base de datos.</p>
                                                <p className="text-gray-400 text-sm mt-1">Sube un archivo CSV usando el panel superior para comenzar.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB: HISTORIAL */}
            {activeTab === 'historial' && (
                <div className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden flex flex-col h-full">
                    <div className="p-5 bg-white border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">Registro Histórico ({MESES[selectedMes]} {selectedAnio})</h3>
                            <p className="text-sm text-gray-500 mt-1">Bitácora detallada de ejecuciones y errores reportados por el robot.</p>
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                            <input
                                type="text"
                                placeholder="Buscar por RUT o Nombre..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="block w-full sm:w-64 pl-3 pr-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                            />
                            <button 
                                onClick={cargarLogs} 
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap"
                            >
                                <FontAwesomeIcon icon={faHistory} className="mr-2" />
                                Refrescar
                            </button>
                        </div>
                    </div>
                    <div className="overflow-x-auto max-h-[700px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 shadow-sm z-10">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Fecha / Hora</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Empresa (RUT)</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Estado</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tipo Declaración</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Diagnóstico / Mensaje</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {Object.values(logs)
                                    .filter(log => selectedEmpresas.has(log.empresaId))
                                    .filter(log => !searchTerm || log.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) || log.rut?.includes(searchTerm))
                                    .sort((a, b) => {
                                        const getPriority = log => {
                                            if (log.estado === 'en_progreso') return 1;
                                            if (log.estado === 'en_cola') return 2;
                                            if (log.estado === 'error') return 3;
                                            return 4;
                                        };
                                        const pA = getPriority(a);
                                        const pB = getPriority(b);
                                        if (pA !== pB) return pA - pB;
                                        return (a.nombre || a.empresaId || '').localeCompare(b.nombre || b.empresaId || '');
                                    })
                                    .map((log, index) => (
                                    <React.Fragment key={index}>
                                        <tr 
                                            className="hover:bg-blue-50/50 transition-colors cursor-pointer"
                                            onClick={() => setExpandedLogId(expandedLogId === log._id ? null : log._id)}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {log.updatedAt ? new Date(log.updatedAt).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                                <div className="flex flex-col">
                                                    <span>{log.nombre || log.empresaId}</span>
                                                    <span className="text-xs text-gray-500 font-normal">{log.rut}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getEstadoBadge(log)}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {log.resultado?.type ? (
                                                    <span className="px-2.5 py-1 text-xs font-semibold rounded-md border bg-gray-50 border-gray-200 text-gray-600">
                                                        {log.resultado.type.replace('_', ' ').toUpperCase()}
                                                    </span>
                                                ) : <span className="text-gray-400 text-xs">-</span>}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 break-words max-w-md">
                                                <div className={`p-2 rounded-md flex justify-between items-center ${log.estado === 'error' ? 'bg-red-50 text-red-800 border border-red-100' : 'bg-gray-50'}`}>
                                                    <span>{log.resultado?.mensaje || (log.estado === 'en_cola' ? 'Esperando turno de ejecución...' : 'Sin detalles')}</span>
                                                    <span className="text-xs text-gray-400 ml-2">{expandedLogId === log._id ? '▲' : '▼'}</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedLogId === log._id && (
                                            <tr>
                                                <td colSpan="5" className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                                    <div className="bg-slate-900 rounded-lg p-4 shadow-inner max-h-[500px] overflow-y-auto">
                                                        <div className="flex justify-between items-center mb-3 border-b border-slate-700 pb-2">
                                                            <h4 className="text-slate-300 text-xs font-bold uppercase tracking-wider">Terminal de Ejecución</h4>
                                                            {log.resultado?.errorScreenshot && (
                                                                <button 
                                                                    onClick={() => {
                                                                        const token = localStorage.getItem('token');
                                                                        window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/f29/descargar-captura/${log._id}?token=${token}`, '_blank');
                                                                    }}
                                                                    className="bg-red-900/50 hover:bg-red-800 text-red-200 border border-red-700 px-3 py-1 rounded text-[10px] uppercase font-bold tracking-wider transition-colors"
                                                                >
                                                                    📸 Ver Captura del Error
                                                                </button>
                                                            )}
                                                        </div>
                                                        {log.logEntries && log.logEntries.length > 0 ? (
                                                            <div className="space-y-1 font-mono text-xs">
                                                                {log.logEntries.map((entry, i) => (
                                                                    <div key={i} className={`${entry.includes('ERROR') || entry.includes('❌') ? 'text-red-400 font-bold' : entry.includes('✅') ? 'text-green-400' : 'text-gray-300'} leading-relaxed`}>{entry}</div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p className="text-slate-500 text-sm italic py-2">No hay logs de terminal guardados para esta ejecución.</p>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                                {Object.values(logs).length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-16 text-center">
                                            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                                <FontAwesomeIcon icon={faHistory} className="text-gray-400 text-xl" />
                                            </div>
                                            <p className="text-gray-500 font-medium">No hay registros históricos para este período.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PanelF29Admin;
